import React, { useMemo } from 'react';
import { parseTimeFrame } from '@/utils/dater';
import { useRouter } from 'expo-router';
import ResourceCard from '../General/ResourceCard';
import Avatar from '../People/Avatar';
import { StyleSheet } from 'react-native-unistyles';

export default function SmallGroupCard({ smallGroup, variant = 'hero', heroImage, avatar }) {
    const router = useRouter();

    const leaderAvatar = useMemo(() => {
        if (avatar) return avatar;
        if (!smallGroup?.Leader) return null;
        return <Avatar disableBorder size={variant === 'compact' ? 36 : 48} profile={smallGroup.Leader} />;
    }, [avatar, smallGroup?.Leader, variant]);

    const statusConfig = useMemo(() => {
        const configs = {
            ACTIVE: { color: "success", label: "Active" },
            COMPLETED: { color: "info", label: "Completed" },
            CANCELED: { color: "error", label: "Canceled" },
            PAUSED: { color: "warning", label: "Paused" },
        };
        return configs[smallGroup?.status] || { color: "textSecondary", label: smallGroup?.status };
    }, [smallGroup?.status]);

    const eventInstances = smallGroup?.Event?.EventInstances || smallGroup?.Sessions?.EventInstances || [];
    const sessionCount = eventInstances.length;
    const lastTaughtString = parseTimeFrame(eventInstances[0]?.startTime, false);
    const studentCount = smallGroup?.Students?.length || 0;
    const leaderNames = [smallGroup?.Leader, smallGroup?.CoLeader]
        .filter(Boolean)
        .map(person => `${person.firstName || ''} ${person.lastName || ''}`.trim())
        .filter(Boolean)
        .join(' and ');
    const route = "/smallgroups/" + smallGroup?.id;

    // Header: avatar + name + status + menu
    const badges = [
        smallGroup?.SmallGroupType?.name && {
            label: smallGroup.SmallGroupType.name,
            colorVariant: 'groups',
            icon: smallGroup.SmallGroupType.icon || 'people-outline',
        },
        statusConfig.label && { label: statusConfig.label, colorVariant: statusConfig.color },
    ].filter(Boolean);

    // Stats row
    const stats = [
        { icon: 'calendar-outline', count: sessionCount, label: `Session${sessionCount !== 1 ? 's' : ''}` },
        { icon: 'people-outline', count: studentCount, label: `Member${studentCount !== 1 ? 's' : ''}` },
    ];

    return (
        <ResourceCard
            variant={variant === 'alternate' ? 'compact' : variant}
            selfSizing={variant === 'alternate'}
            flat={variant === 'alternate'}
            style={variant === 'alternate' ? styles.alternate : undefined}
            heroImage={heroImage}
            avatar={leaderAvatar}
            title={smallGroup?.name}
            subtitle={leaderNames ? `Led by ${leaderNames}` : undefined}
            date={sessionCount > 0 ? lastTaughtString : undefined}
            badges={badges}
            stats={stats}
            moduleColor="groups"
            onPress={() => router.push(route)}
            menuOptions={[
                { name: "Log a Session", onSelect: () => router.push({ pathname: "/smallgroups/sessions/create", params: { smallGroupID: smallGroup?.id } }) },
                { name: "Edit", onSelect: () => router.push({ pathname: "/smallgroups/create", params: { id: smallGroup?.id } }) },
            ]}
            testID="SmallGroupCard"
        />
    );
}

const styles = StyleSheet.create(({ colors, spacing }) => ({
    alternate: {
        backgroundColor: colors.surfaceVariant,
        marginBottom: spacing.sm,
    },
}));
